import { useParams } from 'react-router-dom'
import { documentationRootGuid } from '@constants'
import { getShapeFromElement } from '@editorUtils'
import produce from 'immer'
import { find, findIndex } from 'lodash-es'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles } from '@queries'
import { postFileMetadata } from '@mutations'

const useResyncFile = (
  onResyncSuccess: ((data: FileItem) => void) | undefined,
  guidToElement: Record<string, ShapeObject | ShapeObjectLine | Rip | Lintel>,
  guidToCrossSection: Record<string, CrossSection>,
) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { mutateAsync: mutateResyncFile, isLoading: isMutatingResyncFile } = useMutation({
    mutationFn: async (fileGuid: string) => {
      const queryKey = getProjectFiles.getKey(projectId, documentationRootGuid)
      const currentData = queryClient.getQueryData<FilesResult>(queryKey)
      const file = find(currentData?.files, { guid: fileGuid })

      if (!file || !file.element_guid) throw new Error('File not found or not linked to an element')

      const newData: FileItemConfig = {
        item_guid: fileGuid,
        name: file.name,
        ...(file?.additional_data || {}),
        _elementData: {
          shape: getShapeFromElement(guidToElement[file?.element_guid]),
          crossSection: guidToCrossSection[file?.element_guid],
        },
      }

      const responseData = await postFileMetadata.request(projectId, documentationRootGuid, newData)
      return responseData
    },
    onSuccess: (data: FileItem) => {
      const queryKey = getProjectFiles.getKey(projectId, documentationRootGuid)

      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return

          const index = findIndex(draft.files, { guid: data.guid })
          if (index !== -1) {
            draft.files[index].name = data.name
            draft.files[index].additional_data = data.additional_data
          }
        }),
      )

      onResyncSuccess?.(data)
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Speichern der Eigenschaften', { variant: 'error' })
    },
  })

  return {
    mutateResyncFile,
    isMutatingResyncFile,
  }
}

export default useResyncFile
